import { useEffect, useMemo, useState } from "react";
import { DeliveryChip } from "./DeliveryChip";
import { Link } from "wouter";
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { FileText, Send } from "lucide-react";
import { COMPANY_DOC_CATEGORIES, paths, type CompanyDoc, type DocSend, type DocTemplate } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { X } from "lucide-react";
import { blanksIn } from "./DocView";
import { BlankFiller, startingValues, unfilled } from "./BlankFiller";
import { DocSendDetails } from "./DocSendDetails";
import { updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { call, CallError } from "../lib/callFunction";
import { useAuth } from "../hooks/useAuth";
import { useAccess } from "../hooks/useAccess";
import { textareaClass } from "./JobEditCard";
import { CompanyDocPicker, useCompanyDocs } from "./CompanyDocPicker";
import { useDocumentSets } from "./DocumentSetsCard";

// "Send documents" on a customer, project or job: pick from the company's
// library (W-9, certificates…) and its saved templates (filled in for this
// customer: a PDF, or a link to review and sign), email them, and see every
// earlier send for this record. The server logs exactly what went to whom.

export const categoryLabel = (id: string) => COMPANY_DOC_CATEGORIES.find((c) => c.id === id)?.label ?? id;
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
/** Dates are stored as UTC midnight of the chosen day. */
export const fmtExpiry = (ms: number) => new Date(ms).toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" });
export function todayUtcMidnight(): number {
  const d = new Date();
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}
/** "expired" / "expires soon" (30 days) / null. */
export function expiryState(expiresAt: number | null): "expired" | "soon" | null {
  if (expiresAt === null) return null;
  const today = todayUtcMidnight();
  if (expiresAt < today) return "expired";
  if (expiresAt < today + 30 * 86_400_000) return "soon";
  return null;
}
const splitEmails = (s: string) =>
  s
    .split(/[\s,;]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type DocRow = CompanyDoc & { id: string };
type SendRow = DocSend & { id: string };

export function SendDocuments({
  tenantId,
  customerId,
  projectId = null,
  jobId = null,
  suggestedEmails = [],
}: {
  tenantId: string;
  /** The customer the documents are for (a job's or project's customer). */
  customerId: string | null;
  projectId?: string | null;
  jobId?: string | null;
  /** Addresses offered as one-tap recipients (the customer's email first). */
  suggestedEmails?: string[];
}) {
  const { user } = useAuth();
  const can = useAccess();
  const mayManage = can("documents");
  const [sends, setSends] = useState<SendRow[]>([]);
  const [company, setCompany] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [templates, setTemplates] = useState<Array<DocTemplate & { id: string }>>([]);
  const [pickedTpl, setPickedTpl] = useState<string[]>([]);
  // Values for each picked template's blanks, and whether to keep them in the template.
  const [fills, setFills] = useState<Record<string, Record<string, string>>>({});
  const [keepFills, setKeepFills] = useState<Record<string, boolean>>({});
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [ccMe, setCcMe] = useState(true);
  const [subject, setSubject] = useState("");
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [message, setMessage] = useState("");
  const [allowExpired, setAllowExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [openSend, setOpenSend] = useState<string | null>(null);

  // The send log for this record: a job's own sends, else the project's, else the customer's.
  const scope = jobId ? (["jobId", jobId] as const) : projectId ? (["projectId", projectId] as const) : customerId ? (["customerId", customerId] as const) : null;
  useEffect(() => {
    if (!scope) return;
    return onSnapshot(
      query(collection(db, paths.tenantDocSends(tenantId)), where(scope[0], "==", scope[1]), orderBy("sentAt", "desc"), limit(50)),
      (snap) => setSends(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocSend) }))),
      () => setSends([]),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, scope?.[0], scope?.[1]]);

  // The library only loads once someone opens the panel.
  const docs: DocRow[] = useCompanyDocs(tenantId, customerId, open);
  const sets = useDocumentSets(open ? tenantId : null);
  useEffect(() => {
    if (open && !company) void getDoc(doc(db, paths.tenant(tenantId))).then((s) => setCompany((s.get("name") as string | undefined) ?? ""));
  }, [open, tenantId, company]);
  // Saved templates (not the company-signed lien paperwork): only once the panel is open.
  useEffect(() => {
    if (!open) return;
    return onSnapshot(
      collection(db, paths.tenantTemplates(tenantId)),
      (s) =>
        setTemplates(
          s.docs
            .map((d) => ({ id: d.id, ...(d.data() as DocTemplate) }))
            .filter((t) => !t.archivedAt && t.signer !== "company")
            .sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)),
        ),
      () => setTemplates([]),
    );
  }, [open, tenantId]);
  const chosenTpl = templates.filter((t) => pickedTpl.includes(t.id));

  const chosen = docs.filter((d) => picked.has(d.id));
  const expiredChosen = chosen.filter((d) => expiryState(d.expiresAt) === "expired");
  const names = [...chosen.map((d) => d.name), ...chosenTpl.map((t) => t.name)];
  const autoSubject = names.length ? `${names.join(", ")}${company ? ` from ${company}` : ""}` : "";
  const total = chosen.length + chosenTpl.length;
  const recent = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of [...suggestedEmails, ...sends.flatMap((s) => s.to)]) {
      const x = e.trim().toLowerCase();
      if (x && EMAIL.test(x) && !seen.has(x)) {
        seen.add(x);
        out.push(x);
      }
    }
    return out.slice(0, 6);
  }, [suggestedEmails, sends]);

  function start() {
    setOpen(true);
    setNote("");
    setError("");
    setPicked(new Set());
    setPickedTpl([]);
    setFills({});
    setKeepFills({});
    setTo(recent[0] ?? "");
    setCc("");
    setSubject("");
    setSubjectEdited(false);
    setMessage("");
    setAllowExpired(false);
  }

  async function send() {
    const toList = splitEmails(to);
    const ccList = splitEmails(cc);
    const bad = [...toList, ...ccList].filter((e) => !EMAIL.test(e));
    if (!total) return setError("Pick at least one document or template.");
    const open = chosenTpl.find((t) => unfilled(t.body, fills[t.id] ?? {}).length);
    if (open) return setError(`Fill in the blanks for ${open.name} first (just above).`);
    if (!toList.length) return setError("Add who it goes to.");
    if (bad.length) return setError(`Check ${bad.join(", ")}: that isn't an email address.`);
    setBusy(true);
    setError("");
    try {
      await call("sendCompanyDocs", {
        tenantId,
        docIds: chosen.map((d) => d.id),
        templateIds: chosenTpl.map((t) => t.id),
        fills: Object.fromEntries(chosenTpl.map((t) => [t.id, fills[t.id] ?? {}])),
        to: toList,
        cc: ccList,
        ccMe,
        subject: (subjectEdited ? subject : autoSubject).trim() || autoSubject,
        message,
        customerId,
        projectId,
        jobId,
        allowExpired,
      }, { timeoutMs: 120_000 });
      // "Use these every time": the values go into the template itself.
      for (const t of chosenTpl.filter((x) => keepFills[x.id] && blanksIn(x.body).length)) {
        const values = fills[t.id] ?? {};
        const body = t.body.replace(/\[[^\]\n]+\]/g, (b) => values[b]?.trim() || b);
        await updateDoc(doc(db, paths.tenantTemplate(tenantId, t.id)), { body, updatedAt: Date.now() }).catch(() => undefined);
      }
      setOpen(false);
      setNote(`Sent ${total === 1 ? names[0]! : `${total} documents`} to ${toList.join(", ")}.${chosenTpl.some((t) => t.requiresSignature) ? " They'll get a link to review and sign." : ""}`);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  const shown = showAll ? sends : sends.slice(0, 5);
  return (
    <Card className="p-0">
      <div className="flex items-center justify-between gap-2 px-4 pt-3">
        <CardTitle className="mb-0">Documents sent ({sends.length})</CardTitle>
        {!open ? (
          <Button variant="ghost" className="px-2 py-1 text-xs" onClick={start}>
            <Send size={13} className="mr-1 inline" /> Send documents
          </Button>
        ) : null}
      </div>
      {note ? <p className="mx-4 mt-2 rounded bg-success/10 px-2 py-1 text-xs text-ink">{note}</p> : null}

      {open ? (
        <div className="mx-4 mt-2 space-y-2 rounded-lg border border-steel/20 bg-cream/40 p-3">
          <div>
            <Label>Documents</Label>
            {sets.length ? (
              <div className="mb-1 flex flex-wrap items-center gap-1">
                <span className="text-[11px] text-steel">Sets:</span>
                {sets.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="rounded-full border border-steel/30 bg-white px-2 py-0.5 text-[11px] text-ink hover:border-steel"
                    title={s.docIds.length === 1 ? "1 document" : `${s.docIds.length} documents`}
                    onClick={() => setPicked((p) => new Set([...p, ...s.docIds.filter((id) => docs.some((d) => d.id === id))]))}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            ) : null}
            <CompanyDocPicker
              docs={docs}
              picked={picked}
              canManage={mayManage}
              tenantId={tenantId}
              customerId={customerId}
              onAdded={(id) => setPicked((p) => new Set([...p, id]))}
              onToggle={(id) =>
                setPicked((p) => {
                  const n = new Set(p);
                  if (n.has(id)) n.delete(id);
                  else n.add(id);
                  return n;
                })
              }
            />
            {expiredChosen.length ? (
              <label className="mt-1 flex items-center gap-2 text-xs text-danger">
                <input type="checkbox" checked={allowExpired} onChange={(e) => setAllowExpired(e.target.checked)} />
                {expiredChosen.map((d) => d.name).join(", ")} {expiredChosen.length === 1 ? "has" : "have"} expired. Send anyway
              </label>
            ) : null}
          </div>
          <div>
            <Label>Templates (filled in for this customer)</Label>
            {templates.length ? (
              <Select
                className="py-1 text-sm"
                value=""
                aria-label="Add a template"
                onChange={(e) => {
                  const id = e.target.value;
                  const t = templates.find((x) => x.id === id);
                  if (!t || pickedTpl.includes(id)) return;
                  setPickedTpl((p) => [...p, id]);
                  setFills((f) => ({ ...f, [id]: f[id] ?? startingValues(t.body) }));
                }}
              >
                <option value="">Add a template…</option>
                {[...new Set(templates.map((t) => t.category))].map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {templates
                      .filter((t) => t.category === cat)
                      .map((t) => (
                        <option key={t.id} value={t.id} disabled={pickedTpl.includes(t.id)}>
                          {t.name}
                          {blanksIn(t.body).length ? ` (${blanksIn(t.body).length} to fill in)` : t.requiresSignature ? " (they sign online)" : ""}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </Select>
            ) : (
              <p className="text-xs text-steel">
                No saved templates yet.{" "}
                <Link href="/templates" className="text-brandorange-dark underline">
                  Save one from the starter library
                </Link>
              </p>
            )}
            {chosenTpl.length ? (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {chosenTpl.map((t) => (
                  <span key={t.id} className="flex items-center gap-1 rounded-full bg-white px-2.5 py-0.5 text-xs text-ink ring-1 ring-steel/20">
                    {t.name}
                    <span className="text-steel">· {t.requiresSignature ? "review and sign link" : "PDF"}</span>
                    {blanksIn(t.body).length ? (
                      <span className={unfilled(t.body, fills[t.id] ?? {}).length ? "text-brandorange-dark" : "text-success"}>
                        · {unfilled(t.body, fills[t.id] ?? {}).length ? `${unfilled(t.body, fills[t.id] ?? {}).length} to fill in` : "filled in"}
                      </span>
                    ) : null}
                    <button type="button" aria-label={`Remove ${t.name}`} className="text-steel hover:text-danger" onClick={() => setPickedTpl((p) => p.filter((x) => x !== t.id))}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            {chosenTpl
              .filter((t) => blanksIn(t.body).length)
              .map((t) => (
                <div key={t.id} className="mt-2 rounded-lg border border-brandorange/40 bg-white p-2">
                  <p className="mb-1 text-xs font-semibold text-ink">Fill in {t.name} for this customer</p>
                  <BlankFiller body={t.body} values={fills[t.id] ?? {}} onChange={(v) => setFills((f) => ({ ...f, [t.id]: v }))} />
                  {can("templates") ? (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-steel">
                      <input type="checkbox" checked={!!keepFills[t.id]} onChange={(e) => setKeepFills((k) => ({ ...k, [t.id]: e.target.checked }))} />
                      Use these every time (save them in the template)
                    </label>
                  ) : null}
                </div>
              ))}
          </div>
          <div>
            <Label>To</Label>
            <Input value={to} placeholder="ap@builder.com, pm@builder.com" onChange={(e) => setTo(e.target.value)} />
            {recent.length ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {recent
                  .filter((e) => !splitEmails(to).includes(e))
                  .map((e) => (
                    <button key={e} className="rounded-full bg-steel/10 px-2 py-0.5 text-[11px] text-ink hover:bg-steel/20" onClick={() => setTo((t) => [...splitEmails(t), e].join(", "))}>
                      + {e}
                    </button>
                  ))}
              </div>
            ) : null}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label>CC (optional)</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} />
            </div>
            <label className="flex items-end gap-2 pb-2 text-xs text-ink">
              <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} />
              Copy me{user?.email ? ` (${user.email})` : ""}
            </label>
          </div>
          <div>
            <Label>Subject</Label>
            <Input
              value={subjectEdited ? subject : autoSubject}
              placeholder="Picks itself from the documents"
              onChange={(e) => {
                setSubject(e.target.value);
                setSubjectEdited(true);
              }}
            />
          </div>
          <div>
            <Label>Message (optional)</Label>
            <textarea className={textareaClass} rows={3} placeholder="Hi, attached as requested." value={message} onChange={(e) => setMessage(e.target.value)} />
          </div>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
          <div className="flex items-center gap-2">
            <Button busy={busy} disabled={!total || (expiredChosen.length > 0 && !allowExpired)} onClick={() => void send()}>
              Send {total > 1 ? `${total} documents` : ""}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-2">
        {shown.map((s) => (
          <div key={s.id} className="border-b border-steel/10 px-4 py-1.5 text-xs last:border-0">
            <div role="button" tabIndex={0} className="block w-full cursor-pointer text-left" aria-expanded={openSend === s.id} onClick={() => setOpenSend(openSend === s.id ? null : s.id)} onKeyDown={(e) => e.key === "Enter" && setOpenSend(openSend === s.id ? null : s.id)}>
            <div className="flex justify-between gap-2">
              <span className={cn("min-w-0 truncate font-semibold", s.status === "sent" ? "text-ink" : "text-danger")}>
                {s.status === "failed" ? "Not sent: " : ""}
                {[...s.docs.map((d) => d.name), ...(s.templates ?? []).map((t) => (t.sign ? `${t.name} (to sign)` : t.name))].join(", ")}
              </span>
              <span className="flex shrink-0 items-center gap-1.5 text-steel">
                {s.status === "sent" ? <DeliveryChip delivery={s.delivery} /> : null}
                {fmtDay(s.sentAt)} · {s.sentByName}
              </span>
            </div>
            <div className="text-steel">
              to {s.to.join(", ")}
              {s.cc.length ? ` · cc ${s.cc.join(", ")}` : ""}
              {!jobId && s.jobId ? (
                <>
                  {" · "}
                  <Link href={`/jobs/${s.jobId}`} className="text-brandorange-dark hover:underline">
                    {s.jobTitle || "job"}
                  </Link>
                </>
              ) : !projectId && s.projectName ? (
                ` · ${s.projectName}`
              ) : null}
              {s.status === "failed" && s.error ? ` · ${s.error}` : ""}
              {s.resentFrom ? " · sent again" : ""}
              <span className="ml-1 text-brandorange-dark">{openSend === s.id ? "· close" : "· open"}</span>
            </div>
            </div>
            {openSend === s.id ? <DocSendDetails tenantId={tenantId} send={s} /> : null}
          </div>
        ))}
        {sends.length === 0 && !open ? <p className="px-4 pb-3 text-xs text-steel">Nothing sent yet. W-9s, certificates of insurance and licenses go out from here.</p> : null}
        {sends.length > 5 ? (
          <button className="block w-full px-4 py-1.5 text-center text-xs text-steel hover:text-ink" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show fewer" : `Show all ${sends.length}`}
          </button>
        ) : null}
      </div>
    </Card>
  );
}
